import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { CONTRACT_SELLER_STEP, contractSellerSteps } from '@/config/constants';
import { useGetContractBySellerQuery } from '@/apis/extended/contractApi';
import { TransactionFlowProgress } from '@/components/ui/TransactionFlowProgress';
import { Loader } from '../Layout/Loader';
import { ContractDetailSellerInfo } from './ContractDetailSellerInfo';
import { ContractDetailSellerPropertyDisclosure } from './ContractDetailSellerPropertyDisclosure';
import { ContractDetailSellerPropertyInfo } from './ContractDetailSellerPropertyInfo';
import { ContractDetailSellerReview } from './ContractDetailSellerReview';
import { ContractDetailSellerSuccess } from './ContractDetailSellerSuccess';

type ContractDetailSellerProps = {
  uid: string;
  embedded?: boolean;
};

export const ContractDetailSeller = ({
  uid,
  embedded = false,
}: ContractDetailSellerProps) => {
  const { data: contract, isLoading } = useGetContractBySellerQuery(uid);

  const [step, setStep] = useState(-1);

  const currentStep = useMemo(() => {
    if (!contract) return CONTRACT_SELLER_STEP.SELLER_INFO;

    // Seller completes information entry WITHOUT signing
    // Signature happens later after negotiations are complete
    if (contract.sellerPropertyInfo?.isStored) {
      return CONTRACT_SELLER_STEP.COMPLETED;
    }
    if (contract.sellerPropertyDisclosure?.isStored) {
      return CONTRACT_SELLER_STEP.PROPERTY_INFO;
    }
    if (contract.sellerInfo?.isStored) {
      return CONTRACT_SELLER_STEP.PROPERTY_DISCLOSURES;
    }

    return CONTRACT_SELLER_STEP.SELLER_INFO;
  }, [contract]);

  useEffect(() => {
    if (!contract || step !== -1) return;

    setStep(currentStep);
  }, [contract, currentStep]);

  if (isLoading) return <Loader isFull />;

  const handleClickStep = (step: number) => {
    if (step > currentStep) return;
    setStep(step);
  };

  return (
    <Box>
      <TransactionFlowProgress
        steps={contractSellerSteps}
        activeStep={step}
        onStepClick={handleClickStep}
      />
      <Box
        sx={{
          mt: embedded ? 3 : 8,
          mx: 'auto',
          maxWidth: embedded ? '100%' : '600px',
        }}
      >
        {step === CONTRACT_SELLER_STEP.SELLER_INFO && (
          <ContractDetailSellerInfo
            uid={uid}
            onNext={() => setStep(CONTRACT_SELLER_STEP.PROPERTY_DISCLOSURES)}
          />
        )}
        {step === CONTRACT_SELLER_STEP.PROPERTY_DISCLOSURES && (
          <ContractDetailSellerPropertyDisclosure
            uid={uid}
            onPrev={() => setStep(CONTRACT_SELLER_STEP.SELLER_INFO)}
            onNext={() => setStep(CONTRACT_SELLER_STEP.PROPERTY_INFO)}
          />
        )}
        {step === CONTRACT_SELLER_STEP.PROPERTY_INFO && (
          <ContractDetailSellerPropertyInfo
            uid={uid}
            onPrev={() => setStep(CONTRACT_SELLER_STEP.PROPERTY_DISCLOSURES)}
            onNext={() => setStep(CONTRACT_SELLER_STEP.COMPLETED)}
          />
        )}
        {step === CONTRACT_SELLER_STEP.COMPLETED && (
          <ContractDetailSellerSuccess />
        )}
      </Box>
    </Box>
  );
};
